import axios, { AxiosResponse } from 'axios'
import {
    MouseEventHandler,
    useRef,
    useEffect,
    useState,
    useCallback,
} from 'react'
import { cloneDeep } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import WebViewer, {
    WebViewerOptions,
    WebViewerInstance,
    Annotations,
} from '@pdftron/webviewer'
import { getQmrAttachmentAnnotations } from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/__generated__/getQmrAttachmentAnnotations'
import {
    getAnnotationsToSave,
    getBoundingBoxCoordinates,
    getXFDFToSave,
} from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/PreviewPanel/PreviewItem/PDFViewer/logic'
import { invalidInspectionPointImageData } from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/logic'

import { getenv } from '~/settings'
import { clearPDFOCR } from '~/redux-modules/dfm-layers/actions'
import {
    selectors as PDFOCRSelectors,
    actions as DFMLayersActions,
} from '~/redux-modules/dfm-layers'
import logger from '~/lib/logger'

import {
    INSPECTION_POINT_BUBBLE,
    INSPECTION_POINT,
    INSPECTION_POINT_SELECTION,
    INSPECTION_POINT_SELECTION_AREA,
    ENABLE_HEADER_VIEW_MODE,
    ENABLE_DEFAULT_HEADER_VIEW_MODE,
    ENABLE_HEADER_EDIT_MODE,
} from './constants'
import ViewerModelConfig from './ViewerModelConfig'
import EditorModelConfig from './EditorModelConfig'
import SchedulerModelConfig from './SchedulerModelConfig'
import ImDfm3dVizViewerModelConfig from './ImDfm3dVizViewerModelConfig'
import ImDfmAdminAnnotateModelConfig from './ImDfmAdminAnnotateModelConfig'
import ImDfmBuilderAnnotateModelConfig from './ImDfmBuilderAnnotateModelConfig'
import { configureToleranceAnnotation } from './ToleranceAnnotation'
import { configureThreadAnnotation } from './ThreadsAnnotation'
import { configureGDTAnnotation } from './GDTAnnotation'
import { configureInspectionPoint } from './InspectionPoint'
import {
    configureStandardTolerance,
    standardToleranceAnnotationProperties,
} from './StandardTolerancesAnnotation'
import {
    configureCertificateOfConformance,
    CertificateOfConformanceProperties,
} from './CertificateOfConformanceNote'
import './styles.css'
import { selectFlags } from './selectors'
import {
    configureMaterialCertification,
    MaterialCertificationAnnotationProperties,
} from './MaterialCertificationNote'
import { configureRedaction } from './RedactionAnnotation'

enum PDFViewModels {
    EDITOR,
    IM_DFM_3D_VIZ_VIEWER,
    IM_DFM_ADMIN_ANNOTATE,
    IM_DFM_BUILDER_ANNOTATE,
    SCHEDULER,
    VIEWER,
}

// path to /core and /ui pdftron folders in public path
const RELEASE_ID = getenv('FICTIV_JS_RELEASE_ID')
const API_BASE = getenv('FICTIV_API_BASE')
const CDN_PATH = getenv('FICTIV_JS_CDN_PATH')
const PDFTRON_PDFNETJS_LIC = atob(getenv('PDFTRON_PDFNETJS_LIC'))

const LOCAL_OVERRIDE_PATH =
    RELEASE_ID === 'localhost' &&
    !API_BASE.includes('localhost') &&
    CDN_PATH.includes('3001') &&
    `${API_BASE}cdn/flat/`

const BASE_URL = LOCAL_OVERRIDE_PATH || CDN_PATH

const PDFTRON_PATH = `${BASE_URL}pdftron`

export type PDFViewerProps = {
    pdfUrl: string
    pdfName?: string
    mode: PDFViewModels
    backTo3dModelButtonOnClick?: MouseEventHandler<HTMLButtonElement>
    setCurrentPdfData?: (arg: string) => void
    setAnnotatedAttachmentBlob?: (url: Blob) => void
    unsavedAnno?: Annotations.Annotation[]
    setUnsavedAnno?: (arg: any) => void
    createInspectionPointsToSave?: any
    fileName?: string
    attachmentId: string
    createInspectionPointsToRemove?: (args: { id: string }) => void
    qmrAttachmentAnnotations?: getQmrAttachmentAnnotations
    qmrAttachmentAnnotationsUrl?: string
    showInvalidInspectionPointModal?: (
        invalidInspectionPoints: number[],
    ) => void
}

type customAnnotationsType = {
    coordinate: any
    boundingBox: any
    text?: string
}

const ViewModelConfigs = {
    [PDFViewModels.EDITOR]: EditorModelConfig,
    [PDFViewModels.IM_DFM_3D_VIZ_VIEWER]: ImDfm3dVizViewerModelConfig,
    [PDFViewModels.IM_DFM_ADMIN_ANNOTATE]: ImDfmAdminAnnotateModelConfig,
    [PDFViewModels.IM_DFM_BUILDER_ANNOTATE]: ImDfmBuilderAnnotateModelConfig,
    [PDFViewModels.SCHEDULER]: SchedulerModelConfig,
    [PDFViewModels.VIEWER]: ViewerModelConfig,
}

const PDFViewer = (props: PDFViewerProps) => {
    const {
        pdfUrl,
        mode,
        setCurrentPdfData,
        setAnnotatedAttachmentBlob,
        unsavedAnno,
        setUnsavedAnno,
        createInspectionPointsToSave,
        fileName,
        attachmentId,
        createInspectionPointsToRemove,
        pdfName,
        qmrAttachmentAnnotations,
        qmrAttachmentAnnotationsUrl,
        showInvalidInspectionPointModal,
    } = props
    const annotationUrl =
        qmrAttachmentAnnotationsUrl ||
        qmrAttachmentAnnotations?.getQmrAttachmentAnnotations
            ?.annotationsDataDownloadUrl
    const {
        userName,
        obfuscateFictivAnnotationAttribution,
        shouldHighLightMaterialCertificationAndCOC,
        attachmentLinkId,
        isPdfocrAutoSanitizationEnabled,
    } = useSelector(PDFOCRSelectors.pdfViewerSelector)

    const { shouldHighLightStdTolerances } = useSelector(selectFlags)

    const webviewerRef = useRef<HTMLDivElement>(null)
    const [newInstance, setNewInstance] = useState<WebViewerInstance | null>(
        null,
    )
    const [documentLoaded, setDocumentLoaded] = useState(false)

    const [defautHeaders, setDefaultHeaders] = useState<object[] | null>(null)

    useEffect(() => {
        if (setUnsavedAnno) {
            setUnsavedAnno([])
        }
    }, [qmrAttachmentAnnotations, qmrAttachmentAnnotationsUrl, setUnsavedAnno])

    let instance: WebViewerInstance

    const xfdfRetriver = useCallback(async () => {
        try {
            if (annotationUrl) {
                const response: AxiosResponse = await axios.get(annotationUrl)

                const xfdfData = response?.data

                if (xfdfData && showInvalidInspectionPointModal) {
                    const invalidInspectionPoints =
                        invalidInspectionPointImageData(xfdfData)

                    showInvalidInspectionPointModal(invalidInspectionPoints)
                }

                return xfdfData
            }
        } catch (error) {
            logger.exception(error, {
                info: 'PDFViewer::Failed on axios.get(annotationUrl) in init',
                attachmentId,
                pdfName,
                fileName,
                annotationUrl,
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [annotationUrl])

    useEffect(() => {
        const init = async () => {
            // getting types out of the way:
            // viewerElement needs to be not-nullable HTMLElement for WebViewer,
            // but webviewerRef.current returns a nullable type
            // and linter complains about using a not null assertion like webviewerRef.current!

            if (!webviewerRef.current) {
                return
            }

            const options: WebViewerOptions = {
                path: PDFTRON_PATH,
                annotationUser: userName,
                fullAPI: mode === PDFViewModels.SCHEDULER,
                enableRedaction: mode === PDFViewModels.SCHEDULER,
                licenseKey: PDFTRON_PDFNETJS_LIC,
                disabledElements: ENABLE_DEFAULT_HEADER_VIEW_MODE,
            }

            const viewerElement: HTMLElement = webviewerRef.current

            instance = await WebViewer(options, viewerElement)

            instance.setHeaderItems(header => {
                setDefaultHeaders(cloneDeep(header.getItems()))
            })

            setNewInstance(instance)

            // register the inspection point annotation before fetching XFDF
            // to prevent race conditions when loading inspection points
            // or exporting annotations
            configureInspectionPoint(
                instance,
                createInspectionPointsToSave,
                createInspectionPointsToRemove,
                attachmentId,
            )

            const { docViewer, annotManager } = instance

            const createAnnotatedAttachmentBlob = async () => {
                if (setAnnotatedAttachmentBlob) {
                    const doc = docViewer.getDocument()

                    const xfdfString = await getXFDFToSave(
                        instance,
                        attachmentId,
                    )

                    const fileData = await doc.getFileData({
                        xfdfString,
                    })
                    const arr = new Uint8Array(fileData)
                    const blob = new Blob([arr], {
                        type: 'application/pdf',
                    })

                    setAnnotatedAttachmentBlob(blob)
                }
            }

            await docViewer.getAnnotationsLoadedPromise()

            docViewer.on('pageComplete', async () => {
                await createAnnotatedAttachmentBlob()
            })

            docViewer.on('documentLoaded', async () => {
                await createAnnotatedAttachmentBlob()
                setDocumentLoaded(true)
            })

            annotManager.on(
                'annotationChanged',
                async (annotations, _action, info) => {
                    let customAnnotaion
                    const currentUser = annotManager.getCurrentUser()

                    if (_action === 'delete' || _action === 'modify') {
                        const annotation = annotations[0]

                        customAnnotaion = annotation
                    }

                    if (_action === 'add' && !info.imported) {
                        const annotation = annotations[0]

                        annotation.setCustomData('attachmentId', attachmentId)
                        customAnnotaion = annotation

                        if (annotation.Subject === INSPECTION_POINT_BUBBLE) {
                            annotation.setCustomData(
                                INSPECTION_POINT_BUBBLE,
                                INSPECTION_POINT,
                            )
                        } else if (
                            annotation.Subject === INSPECTION_POINT_SELECTION
                        ) {
                            //the annotation user name is that of the current user or the OBO'd user
                            annotation.setCustomData(
                                INSPECTION_POINT_SELECTION,
                                INSPECTION_POINT_SELECTION_AREA,
                            )
                        } else {
                            annotation.setCustomData('userName', currentUser)
                        }
                        // the annotation user's employee role
                        if (obfuscateFictivAnnotationAttribution) {
                            annotation.setCustomData('isFictiv', false)
                        } else {
                            annotation.setCustomData('isFictiv', true)
                        }
                    }

                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    const mapNames = annotation => {
                        if (annotation.Subject === INSPECTION_POINT_BUBBLE) {
                            return `${annotation.getCustomData(
                                'index',
                            )} - ${annotation.getCustomData(
                                INSPECTION_POINT_BUBBLE,
                            )}`
                        }

                        if (annotation.Subject === INSPECTION_POINT_SELECTION) {
                            return annotation.getCustomData(
                                INSPECTION_POINT_SELECTION,
                            )
                        }
                        // Customers can see Fictiv employee annotations as authored by The Company
                        if (obfuscateFictivAnnotationAttribution) {
                            if (annotation.getCustomData('isFictiv')) {
                                return 'Fictiv'
                            } else {
                                // Customers can see non-Fictiv employee annotations as originally attributed
                                return annotation.getCustomData('userName')
                            }
                        } else {
                            // Fictiv employees can see the name
                            return annotation.getCustomData('userName')
                        }
                    }

                    annotManager.setAnnotationDisplayAuthorMap(mapNames)
                    const xfdf = customAnnotaion
                        ? await getXFDFToSave(instance, attachmentId)
                        : ''
                    const annotationsToSave = getAnnotationsToSave(
                        instance,
                        attachmentId,
                    )

                    if (
                        !info.imported &&
                        setUnsavedAnno &&
                        annotationsToSave.length
                    ) {
                        setUnsavedAnno(
                            (unsavedAnnotations: Annotations.Annotation[]) => {
                                return [
                                    ...unsavedAnnotations,
                                    ...annotationsToSave,
                                ]
                            },
                        )
                    }

                    if (setCurrentPdfData) {
                        setCurrentPdfData(xfdf)
                    }
                },
            )
        }

        init()

        // Safely destroy the WebViewer instance during component cleanup
        return function cleanup() {
            if (instance) {
                return instance.dispose()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (newInstance && pdfUrl) {
            newInstance.docViewer.setDocumentXFDFRetriever(async () => {
                return await xfdfRetriver()
            })
        }
        newInstance?.loadDocument(pdfUrl)
    }, [mode, newInstance, pdfUrl, xfdfRetriver])

    useEffect(() => {
        if (newInstance) {
            newInstance.annotManager.setCurrentUser(userName)
        }
    }, [newInstance, userName])

    const fetchAndUpdateXfdf = useCallback(async () => {
        if (newInstance && documentLoaded) {
            const { annotManager } = newInstance

            try {
                const xfdfString = await xfdfRetriver()

                if (xfdfString) {
                    annotManager.importAnnotations(xfdfString)
                }
            } catch (error) {
                logger.exception(error, {
                    extra: {
                        info: 'failed to load xfdf annotations',
                    },
                })
            }
        }
    }, [newInstance, documentLoaded, xfdfRetriver])

    useEffect(() => {
        fetchAndUpdateXfdf()
    }, [fetchAndUpdateXfdf])

    const dispatch = useDispatch()

    useEffect(() => {
        if (setUnsavedAnno) {
            setUnsavedAnno([])
        }

        const deleteAnno = async () => {
            if (newInstance && unsavedAnno?.length) {
                await newInstance.annotManager.deleteAnnotation(unsavedAnno, {
                    force: true,
                })
            }
        }

        if (newInstance) {
            newInstance.disableElements(ENABLE_DEFAULT_HEADER_VIEW_MODE)

            if (documentLoaded) {
                newInstance.enableElements(
                    mode === PDFViewModels.EDITOR
                        ? ENABLE_HEADER_EDIT_MODE
                        : ENABLE_HEADER_VIEW_MODE,
                )

                newInstance.setZoomLevel(1)

                dispatch(DFMLayersActions.setWebviewerInstance(newInstance))

                const config = ViewModelConfigs[mode]

                config(newInstance, props, defautHeaders)
            }
        }

        deleteAnno()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, newInstance, documentLoaded, pdfName])

    const PDFOCRData = useSelector(PDFOCRSelectors.PDFOCRDataSelector)
    const tolerancesData = PDFOCRData.tolerances as customAnnotationsType[]
    const threadsData = PDFOCRData.threads as customAnnotationsType[]
    const GDTData = PDFOCRData.GDTTolerances as customAnnotationsType[]
    const standardTolerancesData = PDFOCRData.standardTolerances || []
    const certificateOfConformanceData =
        PDFOCRData.certificateOfConformanceWithBoundingBox || []
    const materialCertificationData =
        PDFOCRData.materialCertificationWithBoundingBox || []
    const pdfOCRDataID = PDFOCRData.attachmentLinkId
    const redactionData =
        isPdfocrAutoSanitizationEnabled && PDFOCRData.redactionBoxes
            ? PDFOCRData.redactionBoxes
            : []
    const stringifyRedactData = JSON.stringify(redactionData)

    useEffect(() => {
        if (
            pdfOCRDataID &&
            attachmentLinkId &&
            attachmentLinkId !== pdfOCRDataID
        ) {
            dispatch(clearPDFOCR())
        }
    }, [dispatch, pdfOCRDataID, attachmentLinkId])

    const document = newInstance?.docViewer?.getDocument?.()

    useEffect(() => {
        if (
            newInstance &&
            (tolerancesData?.length ||
                threadsData?.length ||
                standardTolerancesData?.length ||
                certificateOfConformanceData?.length ||
                materialCertificationData?.length ||
                redactionData?.length)
        ) {
            const ToleranceAnnotationClass =
                configureToleranceAnnotation(newInstance)

            const GDTAnnotationClass = configureGDTAnnotation(newInstance)

            const ThreadsAnnotationClass =
                configureThreadAnnotation(newInstance)

            const StandardToleranceAnnotationClass =
                configureStandardTolerance(newInstance)
            const CertificateOfConformanceAnnotationClass =
                configureCertificateOfConformance(newInstance)
            const MaterialCertificationAnnotationClass =
                configureMaterialCertification(newInstance)
            const RedactionAnnotationClass = configureRedaction(
                newInstance,
                attachmentId,
            )

            if (!document) {
                return
            }
            const pdfRotated = newInstance.docViewer.getCompleteRotation(1)

            document
                .documentCompletePromise()
                .then(() => {
                    const threadsAnnotations = threadsData.map(threadData => {
                        const { coordinate, boundingBox } = threadData
                        const pageNumber = boundingBox.pageNumber
                        const pageWidth =
                            newInstance.docViewer.getPageWidth(pageNumber)
                        const pageHeight =
                            newInstance.docViewer.getPageHeight(pageNumber)

                        const boundingBoxParsed = getBoundingBoxCoordinates(
                            pageWidth,
                            pageHeight,
                            boundingBox,
                            pdfRotated,
                        )

                        const coordinateParsed = {
                            page: coordinate.page,
                            x: boundingBoxParsed.xmax,
                            y: boundingBoxParsed.ymax,
                        }

                        const threadAnnotation = new ThreadsAnnotationClass({
                            ...coordinateParsed,
                            boundingBox: boundingBoxParsed,
                        })

                        return threadAnnotation
                    })

                    const toleranceAnnotations = tolerancesData.map(
                        (ocrObject: any) => {
                            const { coordinate, boundingBox } = ocrObject
                            const pageNumber = boundingBox.pageNumber
                            const pageWidth =
                                newInstance.docViewer.getPageWidth(pageNumber)
                            const pageHeight =
                                newInstance.docViewer.getPageHeight(pageNumber)
                            const boundingBoxParsed = getBoundingBoxCoordinates(
                                pageWidth,
                                pageHeight,
                                boundingBox,
                                pdfRotated,
                            )

                            const coordinateParsed = {
                                page: coordinate.page,
                                x: boundingBoxParsed.xmax,
                                y: boundingBoxParsed.ymax,
                            }

                            const toleranceAnnotation =
                                new ToleranceAnnotationClass({
                                    ...coordinateParsed,
                                    boundingBox: boundingBoxParsed,
                                })

                            return toleranceAnnotation
                        },
                    )

                    const GDTAnnotations =
                        GDTData?.map(ocrObject => {
                            const { coordinate, boundingBox } = ocrObject
                            const pageNumber = boundingBox.pageNumber
                            const pageWidth =
                                newInstance.docViewer.getPageWidth(pageNumber)
                            const pageHeight =
                                newInstance.docViewer.getPageHeight(pageNumber)
                            const boundingBoxParsed = getBoundingBoxCoordinates(
                                pageWidth,
                                pageHeight,
                                boundingBox,
                                pdfRotated,
                            )

                            const coordinateParsed = {
                                page: coordinate.page,
                                x: boundingBoxParsed.xmax,
                                y: boundingBoxParsed.ymax,
                            }

                            const GDTAnnotation = new GDTAnnotationClass({
                                ...coordinateParsed,
                                boundingBox: boundingBoxParsed,
                                symbol: ocrObject.text,
                            })

                            return GDTAnnotation
                        }) || []

                    const standardToleranceAnnotations =
                        shouldHighLightStdTolerances
                            ? standardTolerancesData.map((ocrObject: any) => {
                                  const { coordinate, boundingBox } = ocrObject
                                  const pageNumber = boundingBox.pageNumber
                                  const pageWidth =
                                      newInstance.docViewer.getPageWidth(
                                          pageNumber,
                                      )
                                  const pageHeight =
                                      newInstance.docViewer.getPageHeight(
                                          pageNumber,
                                      )
                                  const boundingBoxParsed =
                                      getBoundingBoxCoordinates(
                                          pageWidth,
                                          pageHeight,
                                          boundingBox,
                                          pdfRotated,
                                      )

                                  const coordinateParsed = {
                                      page: coordinate.page,
                                      x: boundingBoxParsed.xmax,
                                      y: boundingBoxParsed.ymax,
                                  }

                                  const toleranceAnnotation =
                                      new StandardToleranceAnnotationClass({
                                          ...coordinateParsed,
                                          boundingBox: boundingBoxParsed,
                                      })

                                  return toleranceAnnotation
                              })
                            : []

                    const certificateOfConformanceAnnotations =
                        shouldHighLightMaterialCertificationAndCOC
                            ? certificateOfConformanceData.map(ocrObject => {
                                  const { coordinate, boundingBox } = ocrObject

                                  const pageNumber = boundingBox.pageNumber
                                  const pageWidth =
                                      newInstance.docViewer.getPageWidth(
                                          pageNumber,
                                      )
                                  const pageHeight =
                                      newInstance.docViewer.getPageHeight(
                                          pageNumber,
                                      )
                                  const boundingBoxParsed =
                                      getBoundingBoxCoordinates(
                                          pageWidth,
                                          pageHeight,
                                          boundingBox,
                                          pdfRotated,
                                      )
                                  const coordinateParsed = {
                                      page: coordinate.page,
                                      x: boundingBoxParsed.xmax,
                                      y: boundingBoxParsed.ymax,
                                  }

                                  const certificateOfConformanceAnnotation =
                                      new CertificateOfConformanceAnnotationClass(
                                          {
                                              ...coordinateParsed,
                                              boundingBox: boundingBoxParsed,
                                          },
                                      )

                                  return certificateOfConformanceAnnotation
                              })
                            : []

                    const materialCerificationAnnotations =
                        shouldHighLightMaterialCertificationAndCOC
                            ? materialCertificationData.map(ocrObject => {
                                  const { coordinate, boundingBox } = ocrObject

                                  const pageNumber = boundingBox.pageNumber
                                  const pageWidth =
                                      newInstance.docViewer.getPageWidth(
                                          pageNumber,
                                      )
                                  const pageHeight =
                                      newInstance.docViewer.getPageHeight(
                                          pageNumber,
                                      )
                                  const boundingBoxParsed =
                                      getBoundingBoxCoordinates(
                                          pageWidth,
                                          pageHeight,
                                          boundingBox,
                                          pdfRotated,
                                      )
                                  const coordinateParsed = {
                                      page: coordinate.page,
                                      x: boundingBoxParsed.xmax,
                                      y: boundingBoxParsed.ymax,
                                  }

                                  const materialCertificationAnnotation =
                                      new MaterialCertificationAnnotationClass({
                                          ...coordinateParsed,
                                          boundingBox: boundingBoxParsed,
                                      })

                                  return materialCertificationAnnotation
                              })
                            : []

                    const redactions = redactionData.map(object => {
                        const { coordinate, boundingBox } = object

                        const pageNumber = boundingBox.pageNumber
                        const pageWidth =
                            newInstance.docViewer.getPageWidth(pageNumber)
                        const pageHeight =
                            newInstance.docViewer.getPageHeight(pageNumber)
                        const boundingBoxParsed = getBoundingBoxCoordinates(
                            pageWidth,
                            pageHeight,
                            boundingBox,
                            pdfRotated,
                        )
                        const coordinateParsed = {
                            page: coordinate.page,
                            x: boundingBoxParsed.xmax,
                            y: boundingBoxParsed.ymax,
                        }

                        return new RedactionAnnotationClass({
                            ...coordinateParsed,
                            boundingBox: boundingBoxParsed,
                        })
                    })

                    newInstance.annotManager.on('annotationChanged', () => {
                        const annotations =
                            newInstance.annotManager.getAnnotationsList()

                        annotations.forEach(annotation => {
                            if (annotation.Subject === 'ToleranceAnnotation') {
                                annotation.draw =
                                    ToleranceAnnotationClass.prototype.draw

                                newInstance.annotManager.redrawAnnotation(
                                    annotation,
                                )
                            }

                            if (
                                annotation.Subject ===
                                    standardToleranceAnnotationProperties.subject &&
                                shouldHighLightStdTolerances
                            ) {
                                annotation.draw =
                                    StandardToleranceAnnotationClass.prototype.draw

                                newInstance.annotManager.redrawAnnotation(
                                    annotation,
                                )
                            }

                            if (
                                annotation.Subject ===
                                    CertificateOfConformanceProperties.subject &&
                                shouldHighLightMaterialCertificationAndCOC
                            ) {
                                annotation.draw =
                                    CertificateOfConformanceAnnotationClass.prototype.draw

                                newInstance.annotManager.redrawAnnotation(
                                    annotation,
                                )
                            }

                            if (
                                annotation.Subject ===
                                    MaterialCertificationAnnotationProperties.subject &&
                                shouldHighLightMaterialCertificationAndCOC
                            ) {
                                annotation.draw =
                                    MaterialCertificationAnnotationClass.prototype.draw

                                newInstance.annotManager.redrawAnnotation(
                                    annotation,
                                )
                            }

                            if (annotation.Subject === 'ThreadAnnotation') {
                                annotation.draw =
                                    ThreadsAnnotationClass.prototype.draw

                                newInstance.annotManager.redrawAnnotation(
                                    annotation,
                                )
                            }
                            if (annotation.Subject === 'GDTAnnotation') {
                                annotation.draw =
                                    GDTAnnotationClass.prototype.draw

                                newInstance.annotManager.redrawAnnotation(
                                    annotation,
                                )
                            }
                        })
                    })

                    if (
                        mode === PDFViewModels.SCHEDULER &&
                        isPdfocrAutoSanitizationEnabled
                    ) {
                        newInstance.annotManager.addAnnotations(redactions, {
                            imported: true,
                        })
                    }
                    newInstance.annotManager.addAnnotations(
                        toleranceAnnotations,
                        {
                            imported: true,
                        },
                    )

                    newInstance.annotManager.addAnnotations(
                        threadsAnnotations,
                        {
                            imported: true,
                        },
                    )

                    newInstance.annotManager.addAnnotations(GDTAnnotations, {
                        imported: true,
                    })

                    dispatch(DFMLayersActions.setWebviewerInstance(newInstance))
                    newInstance.annotManager.addAnnotations(
                        standardToleranceAnnotations,
                        {
                            imported: true,
                        },
                    )
                    newInstance.annotManager.addAnnotations(
                        certificateOfConformanceAnnotations,
                        {
                            imported: true,
                        },
                    )
                    newInstance.annotManager.addAnnotations(
                        materialCerificationAnnotations,
                        {
                            imported: true,
                        },
                    )
                    return true
                })
                .catch(e => {
                    logger.exception(e, {
                        info: 'Failed to load pdf document',
                    })
                })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        newInstance,
        tolerancesData,
        document,
        PDFOCRData,
        standardTolerancesData,
        threadsData,
        shouldHighLightStdTolerances,
        dispatch,
        certificateOfConformanceData,
        materialCertificationData,
        shouldHighLightMaterialCertificationAndCOC,
        stringifyRedactData,
        mode,
        isPdfocrAutoSanitizationEnabled,
    ])

    return (
        <div
            className="pdfviewer"
            style={{
                width: '100%',
                height: '100%',
            }}
        >
            <div
                className="webviewer"
                ref={webviewerRef}
                style={{
                    height: '100%',
                    width: '100%',
                }}
            />
        </div>
    )
}

export { PDFViewModels }
export default PDFViewer
